import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, UserPlus, Search } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Attendee selector. Searches existing People and lets the user add a brand-new
 * name (which the backend turns into a People profile + Vault note).
 *
 * Props:
 *  - value: string[]   selected attendee names
 *  - onChange(names)   called with the next list (additions always; removals only when removable)
 *  - removable         show the chip "x" (default true)
 *  - autoFocus         focus the input on mount
 */
export const AttendeePicker = ({ value = [], onChange, removable = true, autoFocus = false, placeholder = "Search people or type a new name…" }) => {
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/people").then((r) => setPeople(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  const lower = value.map((v) => v.toLowerCase());
  const term = q.trim().toLowerCase();

  const matches = useMemo(() => {
    if (!term) return [];
    return people
      .filter((p) => p.name && p.name.toLowerCase().includes(term) && !lower.includes(p.name.toLowerCase()))
      .slice(0, 6);
  }, [term, people, value]);

  const exactExists =
    people.some((p) => p.name && p.name.toLowerCase() === term) || lower.includes(term);

  const add = (name) => {
    const n = (name || "").trim();
    if (!n || lower.includes(n.toLowerCase())) { setQ(""); setOpen(false); return; }
    onChange([...value, n]);
    setQ("");
    setOpen(false);
    inputRef.current?.focus();
  };
  const remove = (name) => onChange(value.filter((v) => v !== name));

  return (
    <div data-testid="attendee-picker">
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
          {value.map((name) => (
            <span
              key={name}
              data-testid={`attendee-chip-${name}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                fontSize: 12.5, fontWeight: 600, padding: "5px 10px", borderRadius: 999,
                background: "rgba(123,111,232,0.12)", border: "1px solid rgba(123,111,232,0.40)",
                color: "var(--ph-accent-purple)",
              }}
            >
              {name}
              {removable && (
                <button
                  onClick={() => remove(name)}
                  data-testid={`remove-attendee-${name}`}
                  aria-label={`Remove ${name}`}
                  style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "inline-flex", color: "var(--ph-accent-purple)" }}
                >
                  <X size={13} />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div style={{ position: "relative" }}>
        <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--ink-dim)" }} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); add(q); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder={placeholder}
          data-testid="attendee-input"
          style={{
            width: "100%", boxSizing: "border-box", padding: "10px 12px 10px 34px",
            borderRadius: 9, border: "1px solid var(--hairline)", background: "var(--panel-2)",
            color: "var(--ink)", fontSize: 13.5, outline: "none",
          }}
        />
        {open && term && (matches.length > 0 || !exactExists) && (
          <div
            data-testid="attendee-dropdown"
            style={{
              position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 30,
              background: "var(--panel)", border: "1px solid var(--hairline)", borderRadius: 10,
              boxShadow: "0 12px 30px rgba(0,0,0,0.18)", overflow: "hidden",
            }}
          >
            {matches.map((p) => (
              <button
                key={p.id}
                onMouseDown={(e) => { e.preventDefault(); add(p.name); }}
                data-testid={`attendee-option-${p.id}`}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                  padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer",
                  color: "var(--ink)", fontSize: 13,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--panel-2)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <span
                  className="avatar-circle"
                  style={{ width: 22, height: 22, fontSize: 10, background: p.color || "#6B5CE7", flexShrink: 0 }}
                >
                  {p.initials || (p.name[0] || "?").toUpperCase()}
                </span>
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                {p.role && <span className="text-dim" style={{ fontSize: 11.5, marginLeft: "auto" }}>{p.role}</span>}
              </button>
            ))}
            {!exactExists && (
              <button
                onMouseDown={(e) => { e.preventDefault(); add(q); }}
                data-testid="attendee-add-new"
                style={{
                  display: "flex", alignItems: "center", gap: 9, width: "100%", textAlign: "left",
                  padding: "10px 12px", background: "rgba(38,198,218,0.06)", border: "none",
                  borderTop: matches.length ? "1px solid var(--hairline-soft)" : "none",
                  cursor: "pointer", color: "var(--ph-product-teal)", fontSize: 13, fontWeight: 600,
                }}
              >
                <UserPlus size={14} /> Add &ldquo;{q.trim()}&rdquo; as a new person
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendeePicker;
